import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Package, History, Upload, ChevronRight, Box, CheckCircle, AlertTriangle, Truck, Trash2, Warehouse, ScanBarcode, SkipForward, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { hasFeatureAccess, isAdmin } = useAuth();
  const [stats, setStats] = useState({
    active_lists: 0,
    completed_today: 0,
    items_picked_today: 0,
    items_missing_today: 0
  });
  const [activeLists, setActiveLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedListForDetails, setSelectedListForDetails] = useState(null);
  
  // Stats items dialog
  const [showStatsItemsDialog, setShowStatsItemsDialog] = useState(false);
  const [statsItemsFilter, setStatsItemsFilter] = useState("picked");
  const [statsItems, setStatsItems] = useState([]);
  const [loadingStatsItems, setLoadingStatsItems] = useState(false);
  
  // Check if user can create/import picking lists
  const canImportPickingLists = isAdmin || hasFeatureAccess("import_picking_lists");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, listsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/picking/picking-lists/active`)
      ]);
      setStats(statsRes.data);
      setActiveLists(listsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteList = async (listId, listName, e) => {
    e.stopPropagation(); // Prevent navigation to picking page
    try {
      await axios.delete(`${API}/picking/picking-lists/${listId}`);
      toast.success(`Plocklista "${listName}" borttagen`);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error("Kunde inte ta bort plocklistan");
    }
  };

  const getProgress = (list) => {
    if (list.total_items === 0) return 0;
    return Math.round(((list.picked_count + list.missing_count) / list.total_items) * 100);
  };

  const openStatsItemsDialog = async (status) => {
    setStatsItemsFilter(status);
    setShowStatsItemsDialog(true);
    setLoadingStatsItems(true);
    try {
      const response = await axios.get(`${API}/stats/items`, { params: { status } });
      setStatsItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching stats items:", error);
      toast.error("Kunde inte hämta produkter");
    } finally {
      setLoadingStatsItems(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Box className="h-8 w-8 text-primary" />
              <h1 className="font-heading text-2xl font-bold uppercase tracking-tight" data-testid="app-title">
                Plocklista
              </h1>
            </div>
            {canImportPickingLists && (
              <Link to="/import">
                <Button className="gap-2" data-testid="nav-import">
                  <Upload className="h-4 w-4" />
                  Importera
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Stats Grid */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-section">
          <Card className="stat-tile" data-testid="stat-active">
            <CardContent className="p-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-primary/10">
                  <Package className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    Aktiva listor
                  </p>
                  <p className="text-3xl font-bold font-heading">{stats.active_lists}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="stat-tile cursor-pointer hover:border-success/50" 
            data-testid="stat-completed"
            onClick={() => navigate('/history?tab=picking')}
          >
            <CardContent className="p-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-success/10">
                  <CheckCircle className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    Färdigställda idag
                  </p>
                  <p className="text-3xl font-bold font-heading">{stats.completed_today}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="stat-tile cursor-pointer hover:border-info/50" 
            data-testid="stat-picked"
            onClick={() => openStatsItemsDialog("picked")}
          >
            <CardContent className="p-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-info/10">
                  <Truck className="h-5 w-5 text-info" />
                </div>
                <div>
                  <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    Plockade idag
                  </p>
                  <p className="text-3xl font-bold font-heading">{stats.items_picked_today}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="stat-tile cursor-pointer hover:border-amber-500/50" 
            data-testid="stat-skipped"
            onClick={() => openStatsItemsDialog("skipped")}
          >
            <CardContent className="p-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-amber-500/10">
                  <SkipForward className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    Överhoppade idag
                  </p>
                  <p className="text-3xl font-bold font-heading">{stats.items_skipped_today || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Active Lists */}
        <section data-testid="active-lists-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
              Aktiva plocklistor
            </h2>
            {canImportPickingLists && (
              <Link to="/import">
                <Button className="gap-2" data-testid="create-new-list-btn">
                  <Upload className="h-4 w-4" />
                  Ny lista
                </Button>
              </Link>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              Laddar...
            </div>
          ) : activeLists.length === 0 ? (
            <Card className="p-12 text-center" data-testid="empty-state">
              <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-heading text-xl font-bold mb-2">Inga aktiva plocklistor</h3>
              {canImportPickingLists ? (
                <>
                  <p className="text-muted-foreground mb-6">
                    Importera en CSV-fil för att skapa en ny plocklista
                  </p>
                  <Link to="/import">
                    <Button className="gap-2" data-testid="empty-import-btn">
                      <Upload className="h-4 w-4" />
                      Importera CSV
                    </Button>
                  </Link>
                </>
              ) : (
                <p className="text-muted-foreground">
                  Vänta på att en administratör skapar en ny plocklista
                </p>
              )}
            </Card>
          ) : (
            <div className="space-y-3">
              {activeLists.map((list) => (
                <Card
                  key={list.id}
                  className="data-card cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => navigate(`/picking/${list.id}`)}
                  data-testid={`picking-list-${list.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading text-lg font-bold">{list.name}</h3>
                          <span className="text-sm text-muted-foreground">
                            {list.total_items} produkter
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex-1 max-w-xs">
                            <Progress value={getProgress(list)} className="h-2" />
                          </div>
                          <span className="text-sm font-medium">
                            {list.picked_count + (list.skipped_count || 0)} / {list.total_items}
                          </span>
                          {(list.skipped_count || 0) > 0 && (
                            <span className="text-sm text-amber-500 flex items-center gap-1">
                              <SkipForward className="h-3 w-3" />
                              {list.skipped_count} överhoppade
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isAdmin && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-muted-foreground hover:text-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedListForDetails(list);
                              setShowDetailsDialog(true);
                            }}
                            data-testid={`details-list-${list.id}`}
                            title="Visa detaljer"
                          >
                            <List className="h-4 w-4" />
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-muted-foreground hover:text-destructive"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`delete-list-${list.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Ta bort plocklista?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Vill du ta bort "{list.name}"? Denna åtgärd kan inte ångras.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Avbryt</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={(e) => handleDeleteList(list.id, list.name, e)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Ta bort
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Details Dialog - Admin only */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-5 w-5" />
              Plockdetaljer - {selectedListForDetails?.name}
            </DialogTitle>
            <DialogDescription>
              Översikt över alla ordrar med referensnummer och status
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[55vh] pr-4">
            <div className="space-y-2">
              {/* Summary stats */}
              <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-muted/50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold">{selectedListForDetails?.total_items || 0}</p>
                  <p className="text-sm text-muted-foreground">Totalt</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-success">{selectedListForDetails?.picked_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Plockade</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-500">{selectedListForDetails?.skipped_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Överhoppade</p>
                </div>
              </div>

              {/* Items table */}
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-2 text-left font-medium">Status</th>
                    <th className="p-2 text-left font-medium">Referensnummer</th>
                    <th className="p-2 text-left font-medium">Artikelnummer</th>
                    <th className="p-2 text-left font-medium">Produkt</th>
                    <th className="p-2 text-center font-medium">Antal</th>
                    <th className="p-2 text-left font-medium">Transportör</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {selectedListForDetails?.items?.map((item) => (
                    <tr key={item.id} className={
                      item.status === 'picked' ? 'bg-success/5' :
                      item.status === 'skipped' ? 'bg-amber-500/5' :
                      ''
                    }>
                      <td className="p-2">
                        {item.status === 'picked' && (
                          <Badge className="bg-success text-white">Plockad</Badge>
                        )}
                        {item.status === 'skipped' && (
                          <Badge className="bg-amber-500 text-white">Överhoppad</Badge>
                        )}
                        {item.status === 'pending' && (
                          <Badge variant="outline">Väntar</Badge>
                        )}
                      </td>
                      <td className="p-2">
                        <span className="font-mono font-bold text-primary">
                          {item.ordernummer || '—'}
                        </span>
                      </td>
                      <td className="p-2 font-mono">{item.artikelnummer}</td>
                      <td className="p-2">{item.produktnamn}</td>
                      <td className="p-2 text-center">{item.antal || 1} st</td>
                      <td className="p-2">
                        {item.transportor && (
                          <Badge variant="outline" className={
                            item.transportor === 'Bring' ? 'text-green-500 border-green-500/50' :
                            item.transportor === 'DSV' ? 'text-blue-500 border-blue-500/50' :
                            ''
                          }>
                            {item.transportor}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Stäng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Items Dialog - Picked/Skipped today */}
      <Dialog open={showStatsItemsDialog} onOpenChange={setShowStatsItemsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              {statsItemsFilter === 'picked' && (
                <>
                  <CheckCircle className="h-5 w-5 text-success" />
                  Plockade produkter
                </>
              )}
              {statsItemsFilter === 'skipped' && (
                <>
                  <SkipForward className="h-5 w-5 text-amber-500" />
                  Överhoppade produkter
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {statsItemsFilter === 'picked' 
                ? `${statsItems.length} produkter plockade`
                : `${statsItems.length} produkter överhoppade`
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Filter tabs */}
          <div className="flex items-center gap-2 border-b pb-3">
            <Button 
              variant={statsItemsFilter === 'picked' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => openStatsItemsDialog('picked')}
              className={statsItemsFilter === 'picked' ? 'bg-success hover:bg-success/90' : 'text-success border-success/50'}
            >
              Plockade ({stats.items_picked_today})
            </Button>
            <Button 
              variant={statsItemsFilter === 'skipped' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => openStatsItemsDialog('skipped')}
              className={statsItemsFilter === 'skipped' ? 'bg-amber-500 hover:bg-amber-500/90' : 'text-amber-500 border-amber-500/50'}
            >
              Överhoppade ({stats.items_skipped_today || 0})
            </Button>
          </div>
          
          <ScrollArea className="max-h-[55vh] pr-4">
            {loadingStatsItems ? (
              <div className="text-center py-8 text-muted-foreground">
                Laddar...
              </div>
            ) : statsItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Inga produkter med denna status
              </div>
            ) : (
              <div className="space-y-2">
                {statsItems.map((item) => (
                  <div
                    key={`${item.list_id}-${item.id}`}
                    className={`p-3 rounded-md border transition-all ${
                      statsItemsFilter === 'picked' 
                        ? 'bg-success/5 border-success/30' 
                        : 'bg-amber-500/5 border-amber-500/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {statsItemsFilter === 'picked' && <CheckCircle className="h-4 w-4 text-success" />}
                          {statsItemsFilter === 'skipped' && <SkipForward className="h-4 w-4 text-amber-500" />}
                          <span className="font-medium">{item.artikelnummer}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                          <span className="font-mono">{item.artikelnummer}</span>
                          {item.ean && <span className="font-mono text-xs">EAN: {item.ean}</span>}
                          <span>{item.antal} st</span>
                          <span className="font-mono">{item.lagerplats || '—'}</span>
                          {item.ordernummer && <span>Order: {item.ordernummer}</span>}
                          {statsItemsFilter === 'picked' && item.picked_location && (
                            <span className="text-success">Plockad från: {item.picked_location}</span>
                          )}
                        </div>
                      </div>
                      <Badge 
                        variant={statsItemsFilter === 'picked' ? 'default' : 'outline'}
                        className={statsItemsFilter === 'skipped' ? 'border-amber-500 text-amber-500' : ''}
                      >
                        {statsItemsFilter === 'picked' ? 'Plockad' : 'Överhoppad'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
